import * as React from 'react';
import {transparentize} from 'polished';
import {useFormikContext} from 'formik';
import styled from 'styled-components';
import {StyledContainer as BaseContainer} from 'containers/Container';
import {getFormattedDate} from 'components/CoursePromo/helpers';
import {DateFormat} from 'components/CoursePromo/types';
import {TariffsValues} from "../types";

const Container = styled(BaseContainer)`
  padding: 0;
`;

export const Title = styled.div`
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 500;
  line-height: 21px;
  letter-spacing: 0.01em;
  color: ${({theme}) => theme.variables.color};
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const ItemTitle = styled.div`
  font-size: 10px;
  font-weight: 500;
  line-height: 13px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
`;

const BalanceBox = styled.div`
  height: 46px;
  border-radius: 5px;
  width: 100%;
  overflow: hidden;
  background-color: ${({theme}) => theme.variables.backgroundColor};
`;

const BalanceProgress = styled.div`
  background-color: ${({theme}) => theme.colors.primary};
  height: 100%;
  border-top-right-radius: 5px;
  border-bottom-right-radius: 5px;
`;

const Item = styled.div<{primary?: boolean}>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  height: 120px;
  border-radius: 5px;
  box-sizing: border-box;

  background: ${({theme, primary}) => primary
    ? theme.colors.primary
    : theme.variables.accentBackgroundColor};
  
  ${ItemTitle} {
    color: ${({theme, primary}) => primary
      ? transparentize(.2, theme.variables.color)
      : transparentize(.6, theme.variables.color)};
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemText = styled.div`
  margin-top: 10px;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
`;

const ItemUsed = styled(ItemText)`
  margin: 0;
`;

const ItemDate = styled.div`
  margin-top: 10px;
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: 29px;
  letter-spacing: 0.01em;
  text-align: left;
`;

/**
 * render tariffs plan component
 * @constructor
 */
export default function Plan(): React.ReactElement {
  const {values} = useFormikContext<Partial<TariffsValues>>();

  /**
   * render tariffs info block
   * @constructor
   */
  function renderInfo() {
    const autoRenewalStatus = values.current?.autoRenewal
      ? 'включено'
      : 'выключено';

    const date = values.current?.paidBy
      && getFormattedDate(values.current.paidBy, DateFormat.Dot);

    const status = date
      ? 'тариф оплачен до:'
      : 'тариф не оплачен';

    return (
      <Item primary>
        <ItemTitle>{status}</ItemTitle>
        {date && (
          <ItemDate>{date}</ItemDate>
        )}
        <ItemText>
          Автопродление:
          {' '}
          {autoRenewalStatus}
        </ItemText>
      </Item>
    );
  }

  /**
   * render tariffs students balance block
   * @constructor
   */
  function renderStudentsBalance() {
    const used = values.current?.used?.students || 0;
    const limit = values.current?.limits?.students || 0;
    const percent = (used * 100) / limit;

    return (
      <Item>
        <ItemHeader>
          <ItemTitle>студенты</ItemTitle>
          <ItemUsed>{used}/{limit}</ItemUsed>
        </ItemHeader>
        <div>
          <BalanceBox>
            <BalanceProgress style={{width: percent+'%'}}/>
          </BalanceBox>
        </div>
      </Item>
    );
  }

  /**
   * render tariffs used block
   * @constructor
   */
  function renderUsed() {
    const used = values.current?.used?.storage || 0;
    const limit = values.current?.limits?.storage || 0;
    const percent = (used * 100) / limit;

    return (
      <Item>
        <ItemHeader>
          <ItemTitle>файловое хранилище</ItemTitle>
          <ItemUsed>{used}/{limit} Гб</ItemUsed>
        </ItemHeader>
        <div>
          <BalanceBox>
            <BalanceProgress style={{width: percent+'%'}}/>
          </BalanceBox>
        </div>
      </Item>
    );
  }

  return (
    <Container>
      <Title>Ваш тарифный план</Title>
      <Content>
        {renderInfo()}
        {renderStudentsBalance()}
        {renderUsed()}
      </Content>
    </Container>
  );
}
